import { LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Declarative, light-DOM marker for a single selection bubble-menu control.
 *
 * Author these as children of `<nile-wysiwyg-editor>` to build the bubble menu
 * shown over a text selection by hand, instead of the built-in five
 * (bold / italic / underline / strike / link) — the same pattern as
 * `<nile-wysiwyg-toolbar-item>`:
 *
 * ```html
 * <nile-wysiwyg-editor bubble-menu>
 *   <nile-wysiwyg-bubble-item name="bold"></nile-wysiwyg-bubble-item>
 *   <nile-wysiwyg-bubble-item name="italic"></nile-wysiwyg-bubble-item>
 *   <nile-wysiwyg-bubble-item divider></nile-wysiwyg-bubble-item>
 *   <nile-wysiwyg-bubble-item name="link" label="Add link"></nile-wysiwyg-bubble-item>
 * </nile-wysiwyg-editor>
 * ```
 *
 * `name` maps to a built-in control (`bold`, `italic`, `underline`, `strike`,
 * `code`, `subscript`, `superscript`, `link`, `bulletList`, `orderedList`,
 * `alignLeft`, `alignCenter`, `alignRight`, `clearFormatting`). `label` and
 * `icon` override the built-in tooltip/glyph; set `divider` for a separator.
 *
 * Any other `name` is dispatched as a raw `wysiwyg-command` (so custom editor
 * commands work) as long as an `icon` is supplied.
 *
 * The element renders nothing itself — the editor discovers it and renders the
 * real control inside the bubble menu's shadow DOM.
 *
 * @tag nile-wysiwyg-bubble-item
 */
@customElement('nile-wysiwyg-bubble-item')
export class NileWysiwygBubbleItem extends LitElement {
  protected createRenderRoot(): this {
    return this;
  }
  protected shouldUpdate(): boolean {
    return false;
  }

  /** Built-in control name, or a raw command name when paired with `icon`. */
  @property({ type: String, reflect: true }) name = '';

  /** Overrides the control's tooltip / accessible label. */
  @property({ type: String, reflect: true }) label = '';

  /** Overrides the control's glyph (a `nile-glyph` name). */
  @property({ type: String, reflect: true }) icon = '';

  /** Renders a separator instead of a control. */
  @property({ type: Boolean, reflect: true }) divider = false;
}

declare global {
  interface HTMLElementTagNameMap {
    'nile-wysiwyg-bubble-item': NileWysiwygBubbleItem;
  }
}
